import React, { useState } from 'react';
import {
  UserCheck,
  ShieldCheck,
  FileText,
  Linkedin,
  Award,
  CheckCircle2,
  CopyX,
  FileCheck2,
  UserX,
  RefreshCw,
  EyeOff,
  Scale,
  ChevronRight,
  Lock,
  XCircle,
  Sparkles
} from 'lucide-react';
import { LandingHeader } from './LandingHeader';
import LandingFooter from './LandingFooter';

interface NavSection {
  id: string;
  title: string;
  icon: React.ElementType;
}

const sections: NavSection[] = [
  { id: 'overview', title: '1. Trusted Network Overview', icon: UserCheck },
  { id: 'why-matters', title: '2. Why Verification Matters', icon: ShieldCheck },
  { id: 'profile-review', title: '3. Profile Information Review', icon: FileText },
  { id: 'professional-validation', title: '4. Professional Validation', icon: ShieldCheck },
  { id: 'external-profiles', title: '5. LinkedIn & External Review', icon: Linkedin },
  { id: 'experience-approach', title: '6. Experience Verification Approach', icon: Award },
  { id: 'quality-standards', title: '7. Profile Quality Standards', icon: CheckCircle2 },
  { id: 'duplicate-prevention', title: '8. Duplicate Profile Prevention', icon: CopyX },
  { id: 'approval-process', title: '9. Profile Approval Process', icon: FileCheck2 },
  { id: 'rejection-grounds', title: '10. Rejection & Removal Grounds', icon: UserX },
  { id: 'ongoing-review', title: '11. Ongoing Profile Audits', icon: RefreshCw },
  { id: 'privacy-control', title: '12. Privacy & Control', icon: EyeOff },
  { id: 'what-makes-different', title: '13. What Makes Nexgent Different', icon: Scale },
];

const qualityChecklist = [
  'Contains authentic, verifiable professional details.',
  'Reflects genuine leadership experience and achievements.',
  'Includes meaningful role descriptions and impact context.',
  'Avoids exaggerated, inflated, or misleading claims.',
  'Remains current, accurate, and up to date.',
  'Strictly complies with Nexgent terms and community policies.',
];

const rejectionReasons = [
  'Provision of false or misleading professional details.',
  'Exaggerated or unverified leadership history.',
  'Detection of duplicate or alternative candidate accounts.',
  'Inability to satisfy profile validation requests.',
  'Suspected bot, automated, or fraudulent registration.',
  'Violation of platform terms or privacy governance.',
];

export const CandidateVerificationPolicy: React.FC = () => {
  const [activeSection, setActiveSection] = useState<string>('overview');

  const scrollToSection = (id: string) => {
    setActiveSection(id);
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-300 font-sans selection:bg-cyan-500 selection:text-slate-950 relative overflow-hidden">
      <LandingHeader/>
      {/* Background Cyber Glow Effects */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[350px] bg-cyan-500/10 blur-[140px] pointer-events-none rounded-full" />
      <div className="absolute top-1/3 right-0 w-[400px] h-[400px] bg-blue-600/10 blur-[150px] pointer-events-none rounded-full" />

      {/* Header Banner */}
      <header className="border-b border-slate-800 bg-slate-900/60 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="px-2.5 py-0.5 text-xs font-mono font-medium bg-cyan-950 text-cyan-400 border border-cyan-800/50 rounded-full tracking-wide">
                TALENT QUALITY & GOVERNANCE
              </span>
              <span className="text-xs text-slate-500">Motherson Infotech Division</span>
            </div>
            <h1 className="text-2xl md:text-3xl font-extrabold text-white tracking-tight">
              Candidate Verification Policy
            </h1>
          </div>
          <div className="flex items-center gap-3 text-xs font-mono text-slate-400 bg-slate-900/80 px-3 py-2 rounded-lg border border-slate-800">
            <UserCheck className="w-4 h-4 text-cyan-400" />
            <span>Curated Executive Ecosystem</span>
          </div>
        </div>
      </header>

      {/* Main Container Layout */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
        
        {/* Navigation Sidebar */}
        <aside className="lg:col-span-4 xl:col-span-3">
          <div className="sticky top-24 bg-slate-900/70 border border-slate-800 rounded-xl p-4 backdrop-blur-md shadow-xl">
            <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3 px-2">
              Policy Navigation
            </h2>
            <nav className="space-y-1">
              {sections.map((item) => {
                const Icon = item.icon;
                const isActive = activeSection === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className={`w-full flex items-center justify-between px-3 py-2.5 text-xs font-medium rounded-lg transition-all text-left ${
                      isActive
                        ? 'bg-cyan-950/60 text-cyan-300 border border-cyan-800/60 shadow-[0_0_15px_rgba(6,182,212,0.15)]'
                        : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'
                    }`}
                  >
                    <div className="flex items-center gap-2.5 truncate">
                      <Icon className={`w-4 h-4 shrink-0 ${isActive ? 'text-cyan-400' : 'text-slate-500'}`} />
                      <span className="truncate">{item.title}</span>
                    </div>
                    <ChevronRight className={`w-3.5 h-3.5 shrink-0 transition-transform ${isActive ? 'text-cyan-400 translate-x-0.5' : 'text-slate-600'}`} />
                  </button>
                );
              })}
            </nav>
          </div>
        </aside>

        {/* Content Area */}
        <div className="lg:col-span-8 xl:col-span-9 space-y-10">
          
          {/* Section 1: Overview */}
          <section id="overview" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <UserCheck className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">1. Maintaining a Trusted Professional Network</h2>
            </div>
            <p className="text-slate-300 leading-relaxed text-sm">
              Nexgent is committed to providing verified corporate recruiters with access to credible, high-quality candidate profiles while ensuring candidates retain granular control over their career visibility.
            </p>
            <p className="text-slate-400 text-xs leading-relaxed">
              To support this standard, candidate profiles undergo a structured review and verification process prior to becoming discoverable across the platform network.
            </p>
          </section>

          {/* Section 2: Why Candidate Verification Matters */}
          <section id="why-matters" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <ShieldCheck className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">2. Why Candidate Verification Matters</h2>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">
              Profile verification achieves distinct outcomes for candidates and corporate hiring partners:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs pt-1">
              <div className="p-3 bg-slate-950/70 border border-slate-800/80 rounded-lg text-slate-300 flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-cyan-400 shrink-0 mt-0.5" />
                <span>Improve profile authenticity and executive presentation.</span>
              </div>
              <div className="p-3 bg-slate-950/70 border border-slate-800/80 rounded-lg text-slate-300 flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-cyan-400 shrink-0 mt-0.5" />
                <span>Maintain a curated, noise-free network of senior professionals.</span>
              </div>
              <div className="p-3 bg-slate-950/70 border border-slate-800/80 rounded-lg text-slate-300 flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-cyan-400 shrink-0 mt-0.5" />
                <span>Prevent duplicate, automated, or misleading registrations.</span>
              </div>
              <div className="p-3 bg-slate-950/70 border border-slate-800/80 rounded-lg text-slate-300 flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-cyan-400 shrink-0 mt-0.5" />
                <span>Enhance recruiter confidence and high-intent engagement.</span>
              </div>
            </div>
            <p className="text-slate-500 text-[11px] font-mono pt-1">
              * Note: Verification assesses profile completeness and quality standards but does not constitute a legal certification or endorsement of candidate capabilities.
            </p>
          </section>

          {/* Section 3: Profile Information Review */}
          <section id="profile-review" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <FileText className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">3. Profile Information Review</h2>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">
              During registration and profile setup, candidates provide professional data points for review:
            </p>
            <div className="p-4 bg-slate-950/80 border border-slate-800 rounded-xl space-y-2 text-xs">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-slate-400">
                <div className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-cyan-400" />
                  <span>Full Legal Name & Primary Contact</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-cyan-400" />
                  <span>Executive Designation & Headline</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-cyan-400" />
                  <span>Industry Sector & Domain Expertise</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-cyan-400" />
                  <span>Current & Previous Leadership Roles</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-cyan-400" />
                  <span>Curriculum Vitae / Exec Resume</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-cyan-400" />
                  <span>Professional Network Profiles</span>
                </div>
              </div>
            </div>
          </section>

          {/* Section 4: Professional Validation */}
          <section id="professional-validation" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <ShieldCheck className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">4. Professional Profile Validation</h2>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">
              Where applicable, Nexgent reviews publicly accessible professional records to support profile accuracy:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
              <div className="p-3.5 bg-slate-950/80 border border-slate-800 rounded-xl space-y-1">
                <div className="text-cyan-400 font-semibold">Career History Alignment</div>
                <div className="text-slate-400">Comparing submitted tenure and executive roles against public professional records.</div>
              </div>
              <div className="p-3.5 bg-slate-950/80 border border-slate-800 rounded-xl space-y-1">
                <div className="text-cyan-400 font-semibold">Industry Credentials</div>
                <div className="text-slate-400">Evaluating domain credentials, certifications, and leadership milestones.</div>
              </div>
            </div>
          </section>

          {/* Section 5: LinkedIn & External Profile Review */}
          <section id="external-profiles" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <Linkedin className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">5. LinkedIn & Professional Profile Review</h2>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">
              When candidates provide a LinkedIn URL or professional portfolio link, Nexgent reviews the profile to assist with information validation.
            </p>
            <div className="p-4 bg-slate-950/80 border border-slate-800 rounded-xl space-y-2 text-xs">
              <div className="text-cyan-400 font-semibold font-mono uppercase">Review Parameters:</div>
              <ul className="text-slate-400 space-y-1.5 list-disc pl-4">
                <li>Profile completeness and consistency across experience claims.</li>
                <li>Alignment of role designations and tenure timelines.</li>
                <li>Relevance to leadership and senior executive hiring tiers.</li>
              </ul>
            </div>
            <p className="text-slate-500 text-[11px] font-mono">
              * Nexgent operates independently and does not require third-party platform endorsement.
            </p>
          </section>

          {/* Section 6: Experience Verification Approach */}
          <section id="experience-approach" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <Award className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">6. Experience Verification Approach</h2>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">
              Our review team evaluates career experience data to assess alignment with platform quality standards:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
              <div className="p-3.5 bg-slate-950/80 border border-slate-800 rounded-xl space-y-1">
                <div className="text-cyan-400 font-semibold">Consistency Analysis</div>
                <div className="text-slate-400">Verifying timeline logic across current and past executive responsibilities.</div>
              </div>
              <div className="p-3.5 bg-slate-950/80 border border-slate-800 rounded-xl space-y-1">
                <div className="text-cyan-400 font-semibold">Management & Impact Context</div>
                <div className="text-slate-400">Reviewing team scale, P&L responsibility, and domain contribution disclosures.</div>
              </div>
            </div>
            <p className="text-slate-400 text-xs">
              Candidates remain solely responsible for maintaining accurate, truthful disclosures throughout their profile.
            </p>
          </section>

          {/* Section 7: Profile Quality Standards */}
          <section id="quality-standards" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <CheckCircle2 className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">7. Profile Quality Standards</h2>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">
              To remain active and discoverable, candidate profiles must adhere to core network standards:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs pt-1">
              {qualityChecklist.map((item, idx) => (
                <div key={idx} className="p-3 bg-slate-950/80 border border-slate-800/80 rounded-xl flex items-start gap-2 text-slate-300">
                  <CheckCircle2 className="w-4 h-4 text-cyan-400 shrink-0 mt-0.5" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </section>

          {/* Section 8: Duplicate Profile Prevention */}
          <section id="duplicate-prevention" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <CopyX className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">8. Duplicate Profile Prevention</h2>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">
              To preserve recruiter search integrity, candidates are permitted to maintain strictly <strong>one active account</strong>.
            </p>
            <div className="p-4 bg-slate-950/80 border border-slate-800 rounded-xl space-y-2 text-xs">
              <div className="text-cyan-400 font-semibold font-mono uppercase">Detection Mechanics:</div>
              <ul className="text-slate-400 space-y-1 list-disc pl-4">
                <li>Automated primary email and contact phone matching.</li>
                <li>Profile similarity analysis across career history data.</li>
                <li>System activity logs and manual compliance reviews.</li>
              </ul>
            </div>
            <p className="text-slate-400 text-xs">
              Duplicate, inactive, or unauthorized secondary accounts may be merged or archived at Nexgent's discretion.
            </p>
          </section>

          {/* Section 9: Profile Approval Process */}
          <section id="approval-process" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <FileCheck2 className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">9. Profile Approval Process</h2>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">
              Following registration, candidate accounts enter a structured administrative review queue before becoming searchable by corporate recruiters:
            </p>
            <div className="p-4 bg-slate-950/80 border border-slate-800 rounded-xl text-xs space-y-2">
              <div className="flex items-center justify-between text-slate-400 border-b border-slate-800 pb-2">
                <span>Evaluation Criteria</span>
                <span className="text-cyan-400 font-mono">Status: Pending Review</span>
              </div>
              <p className="text-slate-300 leading-relaxed">
                Reviews examine data completeness, profile presentation quality, and adherence to platform privacy guidelines. Additional clarifying details may be requested if needed.
              </p>
            </div>
          </section>

          {/* Section 10: Profile Rejection or Removal */}
          <section id="rejection-grounds" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <UserX className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">10. Grounds for Rejection or Removal</h2>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">
              Nexgent reserves the right to decline, suspend, or remove profiles under the following conditions:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs pt-1">
              {rejectionReasons.map((reason, idx) => (
                <div key={idx} className="p-3 bg-slate-950/80 border border-slate-800/80 rounded-xl flex items-start gap-2 text-slate-300">
                  <XCircle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                  <span>{reason}</span>
                </div>
              ))}
            </div>
          </section>

          {/* Section 11: Ongoing Profile Review */}
          <section id="ongoing-review" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <RefreshCw className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">11. Ongoing Profile Review & Audits</h2>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">
              Validation is an ongoing operational commitment. Nexgent periodically audits active candidate profiles to ensure continued data precision, update stale records, and maintain ecosystem trust.
            </p>
          </section>

          {/* Section 12: Privacy and Professional Control */}
          <section id="privacy-control" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <EyeOff className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">12. Privacy and Professional Control</h2>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">
              Candidate verification directly reinforces privacy protection. By ensuring that only legitimate professionals participate, candidates can evaluate opportunities confidently with granular visibility controls.
            </p>
          </section>

          {/* Section 13: What Makes Nexgent Different */}
          <section id="what-makes-different" className="scroll-mt-28 space-y-6 bg-slate-900/80 p-6 md:p-8 rounded-2xl border border-cyan-800/50 shadow-[0_0_30px_rgba(6,182,212,0.1)]">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-cyan-950 text-cyan-400 rounded-lg border border-cyan-800">
                <Scale className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">13. What Makes Nexgent Different</h2>
                <p className="text-xs text-slate-400">Comparing network architectures</p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 text-xs pt-1">
              <div className="p-3.5 bg-slate-950 rounded-xl border border-slate-800 space-y-1">
                <div className="text-cyan-400 font-semibold flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5" />
                  <span>Administrative Review</span>
                </div>
                <p className="text-slate-400 text-[11px] leading-relaxed">Pre-approval validation vs immediate public exposure.</p>
              </div>

              <div className="p-3.5 bg-slate-950 rounded-xl border border-slate-800 space-y-1">
                <div className="text-cyan-400 font-semibold flex items-center gap-1.5">
                  <Lock className="w-3.5 h-3.5" />
                  <span>Privacy First</span>
                </div>
                <p className="text-slate-400 text-[11px] leading-relaxed">Candidate-controlled visibility settings and anonymized search cards.</p>
              </div>

              <div className="p-3.5 bg-slate-950 rounded-xl border border-slate-800 space-y-1">
                <div className="text-cyan-400 font-semibold flex items-center gap-1.5">
                  <CopyX className="w-3.5 h-3.5" />
                  <span>Duplicate Prevention</span>
                </div>
                <p className="text-slate-400 text-[11px] leading-relaxed">Single active account enforcement for clean search metrics.</p>
              </div>
            </div>

            <div className="p-3 bg-slate-950/40 rounded-lg border border-slate-800/80 text-center text-xs text-cyan-500 font-mono mt-4">
              {/* © {new Date().getFullYear()} Motherson Infotech / Nexgent Governance. All rights reserved. */}
              Nexgent Trust & Governance Framework • Verified Profiles • Private Access • Privacy First

            </div>
          </section>

        </div>
      </main>
      <LandingFooter/>
    </div>
  );
};

export default CandidateVerificationPolicy;